import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import StandardProcessForm from "./StandardProcessForm";
import LooptFoundationForm from "./LooptFoundationForm";
import RagTraderForm from "./RagTraderForm";
import ValueChannelForm from "./ValueChannelForm";

const MultiStepForm = ({
  currentStep,
  steps,
  formData,
  errors,
  onInputChange,
  isValidating,
  onStepNext,
  onStepPrevious,
  isFormValid,
}) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    // Calculate progress to reach exactly to each step icon
    // For 4 steps: step 1 = 0%, step 2 = 33.33%, step 3 = 66.66%, step 4 = 100%
    const targetProgress =
      currentStep === 1 ? 0 : ((currentStep - 1) / (steps.length - 1)) * 100;
    let start = progress;
    const duration = 1000; // 1 second transition
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const animationProgress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const eased =
        animationProgress < 0.5
          ? 4 * animationProgress * animationProgress * animationProgress
          : 1 - Math.pow(-2 * animationProgress + 2, 3) / 2;

      setProgress(start + (targetProgress - start) * eased);

      if (animationProgress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [currentStep, steps.length]);

  useEffect(() => {
    if (isCompleting) {
      const timer = setTimeout(() => {
        navigate("/calculation-processing");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isCompleting, navigate]);

  const handleStepNext = () => {
    if (currentStep === steps.length) {
      setIsCompleting(true);
    }
    onStepNext();
  };

  const renderStepForm = () => {
    switch (currentStep) {
      case 1:
        return (
          <StandardProcessForm
            formData={formData}
            errors={errors}
            onInputChange={onInputChange}
            isValidating={isValidating}
          />
        );
      case 2:
        return (
          <LooptFoundationForm
            formData={formData}
            errors={errors}
            onInputChange={onInputChange}
            isValidating={isValidating}
          />
        );
      case 3:
        return (
          <RagTraderForm
            formData={formData}
            errors={errors}
            onInputChange={onInputChange}
            isValidating={isValidating}
          />
        );
      case 4:
        return (
          <ValueChannelForm
            formData={formData}
            errors={errors}
            onInputChange={onInputChange}
            isValidating={isValidating}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Progress Indicator */}
      <div className="relative mb-8">
        {/* Background Line */}
        <div className="absolute top-4 left-4 right-4 h-0.5 bg-border"></div>

        {/* Progress Line */}
        <div className="absolute top-4 left-4 right-4 h-0.5 overflow-hidden">
          <div
            className="h-full bg-success transition-all duration-1000 ease-in-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Step Icons */}
        <div className="relative flex justify-between">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-500 ${
                step.id < currentStep
                  ? "border-success bg-success text-white scale-110"
                  : step.id === currentStep
                  ? "border-primary bg-surface text-primary scale-110"
                  : "border-border bg-surface text-text-tertiary scale-100"
              }`}
            >
              <div className="transition-all duration-300">
                {step.id < currentStep ? (
                  <Icon name="Check" size={16} className="animate-scale-in" />
                ) : (
                  <span className="text-sm font-body-medium">{step.id}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="min-h-[400px] transition-all duration-300">
        {renderStepForm()}
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-between pt-6 border-t border-border-light">
        <button
          onClick={onStepPrevious}
          disabled={isCompleting}
          className="inline-flex items-center px-6 py-3 bg-surface text-text-primary rounded-lg font-body-medium hover:bg-surface-100 border border-border hover:border-primary transition-all duration-150 hover-scale focus-ring disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon name="ArrowLeft" size={20} className="mr-2" />
          {currentStep === 1 ? "Intro" : "Previous"}
        </button>

        <div className="flex items-center space-x-4">
          {/* Form Status Indicator */}
          <div className="hidden sm:flex items-center space-x-2 text-sm">
            {isFormValid ? (
              <>
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-success font-body-medium">
                  Step complete
                </span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-warning rounded-full"></div>
                <span className="text-warning font-body-medium">
                  Complete required fields
                </span>
              </>
            )}
          </div>

          <button
            onClick={handleStepNext}
            disabled={!isFormValid || isValidating || isCompleting}
            className={`inline-flex items-center px-6 py-3 rounded-lg font-body-medium transition-all duration-300 hover-scale focus-ring shadow-elevation-1 ${
              isFormValid && !isValidating && !isCompleting
                ? "bg-primary text-white hover:bg-primary-600"
                : "bg-surface-100 text-text-tertiary cursor-not-allowed border border-border"
            }`}
          >
            {isValidating ? (
              <>
                <div className="animate-spin mr-2">
                  <Icon name="Loader2" size={20} />
                </div>
                Validating...
              </>
            ) : isCompleting ? (
              <>
                <div className="animate-spin mr-2">
                  <Icon name="Loader2" size={20} />
                </div>
                Completing...
              </>
            ) : currentStep === steps.length ? (
              <>
                Complete Setup
                <Icon name="Check" size={20} className="ml-2" />
              </>
            ) : (
              <>
                Next
                <Icon name="ArrowRight" size={20} className="ml-2" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MultiStepForm;
